import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Play, Pause, Square } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'analytica_timer_state';
const SAVE_INTERVAL = 10; // Salvar no localStorage a cada 10 segundos

export default function Timer({ topicId, topicTitle, onSessionComplete, compact = false }) {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);      // Momento em que o timer começou/retomou
  const baseElapsedRef = useRef(0);       // Tempo acumulado antes de pausar
  const lastSaveRef = useRef(0);          // Último segundo em que salvamos

  // Função para limpar o intervalo de forma segura
  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Função para salvar estado no localStorage
  const saveToStorage = useCallback((state) => {
    const data = {
      topicId,
      sessionId: state.sessionId,
      elapsedTime: state.elapsedTime,
      baseElapsed: state.baseElapsed || 0,
      startTime: state.startTime || null,
      isRunning: state.isRunning,
      isPaused: state.isPaused,
      savedAt: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [topicId]);

  // Função para limpar localStorage
  const clearStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Restaurar estado do localStorage ao montar
  useEffect(() => {
    const loadSavedState = async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        
        if (saved) {
          const data = JSON.parse(saved);
          
          // Verificar se é do mesmo tópico
          if (data.topicId === topicId && data.sessionId) {
            setSessionId(data.sessionId);
            setIsRunning(data.isRunning);
            setIsPaused(data.isPaused);
            
            if (data.isRunning && !data.isPaused && data.startTime) {
              // Timer estava rodando - calcular tempo adicional desde o save
              const additionalTime = Math.floor((Date.now() - data.savedAt) / 1000);
              const totalElapsed = (data.baseElapsed || 0) + additionalTime;
              setElapsedTime(totalElapsed);
              baseElapsedRef.current = totalElapsed;
              startTimeRef.current = Date.now();
            } else {
              // Timer estava pausado - restaurar tempo exato
              setElapsedTime(data.elapsedTime || 0);
              baseElapsedRef.current = data.elapsedTime || 0;
            }
            
            return;
          }
        }
        
        // Verificar sessão ativa no backend se não há dados locais
        const response = await api.get('/sessions/active');
        if (response.data.session && response.data.session.topic_id === topicId) {
          const session = response.data.session;
          setSessionId(session.id);
          setIsRunning(true);
          setIsPaused(false);
          
          const elapsed = Math.floor((Date.now() - new Date(session.started_at).getTime()) / 1000);
          setElapsedTime(Math.max(0, elapsed));
          baseElapsedRef.current = elapsed;
          startTimeRef.current = Date.now();
          
          saveToStorage({
            sessionId: session.id,
            elapsedTime: elapsed,
            baseElapsed: elapsed,
            startTime: Date.now(),
            isRunning: true,
            isPaused: false
          });
        } else {
          clearStorage();
        }
      } catch (error) {
        console.error('[Timer] Erro ao verificar sessão:', error);
      }
    };
    
    loadSavedState();
    
    return () => clearTimer();
  }, [topicId, saveToStorage, clearStorage, clearTimer]);

  // Efeito principal do cronômetro - usa Date.now() para precisão
  useEffect(() => {
    clearTimer();
    
    if (isRunning && !isPaused) {
      startTimeRef.current = Date.now();
      
      intervalRef.current = setInterval(() => {
        // Calcular tempo real usando Date.now()
        const now = Date.now();
        const elapsedSinceStart = Math.floor((now - startTimeRef.current) / 1000);
        const totalElapsed = baseElapsedRef.current + elapsedSinceStart;
        
        setElapsedTime(totalElapsed);
        
        // Salvar no localStorage apenas a cada SAVE_INTERVAL segundos
        if (totalElapsed - lastSaveRef.current >= SAVE_INTERVAL) {
          lastSaveRef.current = totalElapsed;
          saveToStorage({
            sessionId,
            elapsedTime: totalElapsed,
            baseElapsed: baseElapsedRef.current,
            startTime: startTimeRef.current,
            isRunning: true,
            isPaused: false
          });
        }
      }, 1000);
    }
    
    return () => clearTimer();
  }, [isRunning, isPaused, sessionId, saveToStorage, clearTimer]);

  // Salvar antes de fechar/recarregar a página
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isRunning && sessionId) {
        // Calcular tempo atual antes de fechar
        const now = Date.now();
        const elapsedSinceStart = Math.floor((now - (startTimeRef.current || now)) / 1000);
        const totalElapsed = isPaused ? elapsedTime : baseElapsedRef.current + elapsedSinceStart;
        
        saveToStorage({
          sessionId,
          elapsedTime: totalElapsed,
          baseElapsed: isPaused ? totalElapsed : baseElapsedRef.current,
          startTime: isPaused ? null : startTimeRef.current,
          isRunning,
          isPaused
        });
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRunning, isPaused, sessionId, elapsedTime, saveToStorage]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleStart = async () => {
    try {
      const response = await api.post('/sessions/start', { topic_id: topicId });
      const newSessionId = response.data.session.id;
      
      setSessionId(newSessionId);
      setIsRunning(true);
      setIsPaused(false);
      setElapsedTime(0);
      
      // Resetar refs
      baseElapsedRef.current = 0;
      startTimeRef.current = Date.now();
      lastSaveRef.current = 0;
      
      saveToStorage({
        sessionId: newSessionId,
        elapsedTime: 0,
        baseElapsed: 0,
        startTime: Date.now(),
        isRunning: true,
        isPaused: false
      });
      
      toast.success('Cronômetro iniciado!');
    } catch (error) {
      console.error('[Timer] Erro ao iniciar:', error);
      toast.error('Erro ao iniciar sessão');
    }
  };

  const handlePause = async () => {
    if (!sessionId) {
      toast.error('Sessão não encontrada');
      return;
    }
    
    // 1. Parar o intervalo imediatamente
    clearTimer();
    
    // 2. Calcular e salvar o tempo final
    const now = Date.now();
    const elapsedSinceStart = Math.floor((now - (startTimeRef.current || now)) / 1000);
    const totalElapsed = baseElapsedRef.current + elapsedSinceStart;
    
    // 3. Atualizar refs e estado
    baseElapsedRef.current = totalElapsed;
    setElapsedTime(totalElapsed);
    setIsPaused(true);
    
    // 4. Salvar no localStorage imediatamente
    saveToStorage({
      sessionId,
      elapsedTime: totalElapsed,
      baseElapsed: totalElapsed,
      startTime: null,
      isRunning: true,
      isPaused: true
    });
    
    // 5. Notificar backend
    try {
      await api.patch(`/sessions/${sessionId}/pause`);
      toast.success('Cronômetro pausado');
    } catch (error) {
      console.error('[Timer] Erro ao pausar:', error);
      toast.error('Erro ao pausar');
    }
  };

  const handleResume = async () => {
    if (!sessionId) {
      toast.error('Sessão não encontrada');
      return;
    }
    
    try {
      await api.patch(`/sessions/${sessionId}/resume`);
      
      // Resetar startTime para agora, manter baseElapsed
      startTimeRef.current = Date.now();
      setIsPaused(false);
      
      saveToStorage({
        sessionId,
        elapsedTime,
        baseElapsed: baseElapsedRef.current,
        startTime: Date.now(),
        isRunning: true,
        isPaused: false
      });
      
      toast.success('Cronômetro retomado');
    } catch (error) {
      console.error('[Timer] Erro ao retomar:', error);
      toast.error('Erro ao retomar');
    }
  };

  const handleStop = async () => {
    if (!sessionId) return;
    
    // 1. Parar o intervalo
    clearTimer();
    
    try {
      const response = await api.post(`/sessions/${sessionId}/stop`);
      
      // 2. Resetar todo o estado
      setIsRunning(false);
      setIsPaused(false);
      setElapsedTime(0);
      setSessionId(null);
      
      // 3. Resetar refs
      baseElapsedRef.current = 0;
      startTimeRef.current = null;
      lastSaveRef.current = 0;
      
      // 4. Limpar localStorage completamente
      clearStorage();
      
      const duration = response.data.session.duration_minutes;
      toast.success(`Sessão de ${duration}min salva!`);
      
      if (onSessionComplete) {
        onSessionComplete(response.data.session);
      }
    } catch (error) {
      console.error('[Timer] Erro ao parar:', error);
      toast.error('Erro ao parar sessão');
    }
  };

  // Renderização compacta
  if (compact) {
    return (
      <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <span className="text-xl font-mono font-bold text-blue-600 dark:text-blue-400" data-testid="timer-display">
          {formatTime(elapsedTime)}
        </span>
        {!isRunning ? (
          <Button size="sm" onClick={handleStart} data-testid="start-timer-btn">
            <Play className="w-4 h-4" />
          </Button>
        ) : (
          <>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={isPaused ? handleResume : handlePause} 
              data-testid="pause-timer-btn"
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </Button>
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={handleStop} 
              data-testid="stop-timer-btn"
            >
              <Square className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    );
  }

  // Renderização completa
  return (
    <div className="bg-white dark:bg-slate-800 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center" data-testid="timer-card">
      <h3 className="text-sm text-slate-600 dark:text-slate-400 mb-2">Tempo de Estudo</h3>
      <div className={`rounded-lg p-6 mb-4 ${isPaused ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
        <div className={`text-5xl font-mono font-bold tabular-nums ${isPaused ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} data-testid="timer-display">
          {formatTime(elapsedTime)}
        </div>
        {isPaused && (
          <div className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium">
            ⏸️ PAUSADO
          </div>
        )}
      </div>
      <div className="flex gap-3 justify-center">
        {!isRunning ? (
          <Button onClick={handleStart} className="px-6" data-testid="start-timer-btn">
            <Play className="w-4 h-4 mr-2" /> Iniciar
          </Button>
        ) : (
          <>
            <Button 
              variant={isPaused ? "default" : "outline"} 
              onClick={isPaused ? handleResume : handlePause} 
              className="px-6" 
              data-testid="pause-timer-btn"
            >
              {isPaused ? (
                <><Play className="w-4 h-4 mr-2" /> Retomar</>
              ) : (
                <><Pause className="w-4 h-4 mr-2" /> Pausar</>
              )}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleStop} 
              className="px-6" 
              data-testid="stop-timer-btn"
            >
              <Square className="w-4 h-4 mr-2" /> Parar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
